/**
 * Debug Report Dialog Component
 *
 * Allows users to submit debug reports with a note about the issue.
 * Captures the current page DOM state when opened.
 */

import { LitElement, html, css } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "@awesome.me/webawesome/dist/components/textarea/textarea.js";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import { debugService, type DebugPageState } from "../services/debug";

@customElement("debug-dialog")
export class DebugDialog extends LitElement {
  @property({ type: Boolean }) open = false;

  @state() private pageState: DebugPageState | null = null;
  @state() private userNote = "";
  @state() private isSubmitting = false;
  @state() private error = "";
  @state() private success = false;
  @state() private generatedMarkdown = "";

  static styles = css`
    :host {
      display: block;
    }

    wa-dialog::part(panel) {
      max-width: min(90vw, 600px);
    }

    .dialog-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
    }

    .dialog-header wa-icon {
      color: #6c757d;
    }

    .dialog-header h2 {
      margin: 0;
      font-size: 1.25rem;
    }

    .form-group {
      margin-bottom: 1rem;
    }

    .form-group label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      font-size: 0.875rem;
    }

    wa-textarea {
      width: 100%;
    }

    .preview-section {
      margin-top: 1rem;
      padding: 1rem;
      background: #f8f9fa;
      border-radius: 0.5rem;
      max-height: 50vh;
      overflow-y: auto;
      border: 1px solid #dee2e6;
    }

    .preview-section h4 {
      margin: 0 0 0.5rem 0;
      font-size: 0.875rem;
      color: #6c757d;
    }

    .preview-content {
      font-family: monospace;
      font-size: 0.75rem;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .actions {
      display: flex;
      gap: 0.5rem;
      justify-content: flex-end;
      margin-top: 1rem;
    }

    .page-name {
      font-size: 0.875rem;
      color: #6c757d;
      margin-bottom: 1rem;
    }

    wa-callout {
      margin-bottom: 1rem;
    }

    .success-content {
      text-align: center;
      padding: 1rem;
    }

    .success-content wa-icon {
      font-size: 3rem;
      color: #198754;
      margin-bottom: 1rem;
    }
  `;

  updated(changed: Map<string, unknown>) {
    if (changed.has("open") && this.open) {
      this.error = "";
      this.success = false;
      this.userNote = "";
      // Capture the current page state from the DOM
      this.pageState = debugService.capturePageState();
      this.generatedMarkdown = debugService.generateMarkdown(this.pageState);
    }
  }

  /**
   * Check if the page has detailed markdown content (implements renderMarkdown)
   */
  private _hasDetailedPageContent(): boolean {
    if (!this.pageState) return false;
    // If the page doesn't implement renderMarkdown, it will contain this message
    return !this.pageState.pageMarkdown.includes(
      "does not implement renderMarkdown",
    );
  }

  private _handleClose() {
    this.dispatchEvent(new CustomEvent("close"));
  }

  private async _handleSubmit() {
    if (!this.pageState) {
      this.error = "No page state captured";
      return;
    }

    if (!this.userNote.trim()) {
      this.error = "Please describe the issue you're experiencing";
      return;
    }

    this.isSubmitting = true;
    this.error = "";

    try {
      const result = await debugService.submitReport(
        this.pageState,
        this.userNote,
      );

      if (result.success) {
        this.success = true;
      } else {
        this.error = result.error || "Failed to submit report";
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "An error occurred";
    } finally {
      this.isSubmitting = false;
    }
  }

  render() {
    return html`
      <wa-dialog
        ?open=${this.open}
        @wa-request-close=${this._handleClose}
        label="Submit Debug Report"
      >
        ${this.success ? this._renderSuccess() : this._renderForm()}
      </wa-dialog>
    `;
  }

  private _renderSuccess() {
    return html`
      <div class="success-content">
        <wa-icon name="circle-check"></wa-icon>
        <h3>Report Submitted</h3>
        <p>
          Thank you for submitting your debug report. This will help us diagnose
          and fix the issue.
        </p>
        <wa-button variant="brand" @click=${this._handleClose}>Close</wa-button>
      </div>
    `;
  }

  private _renderForm() {
    return html`
      <div class="dialog-header">
        <wa-icon name="bug"></wa-icon>
        <h2>Submit Debug Report</h2>
      </div>

      ${this.pageState
        ? html`<p class="page-name">
            Page: <strong>${this.pageState.pageName}</strong>
          </p>`
        : ""}
      ${this.error
        ? html`
            <wa-callout variant="danger" open>
              <wa-icon slot="icon" name="circle-exclamation"></wa-icon>
              ${this.error}
            </wa-callout>
          `
        : ""}

      <div class="form-group">
        <label for="user-note">Describe the issue you're experiencing:</label>
        <wa-textarea
          id="user-note"
          rows="4"
          placeholder="What's not working correctly? What did you expect to happen?"
          .value=${this.userNote}
          @input=${(e: Event) => {
            const input = e.target as any;
            this.userNote = input.value;
          }}
          ?disabled=${this.isSubmitting}
        ></wa-textarea>
      </div>

      ${this._hasDetailedPageContent()
        ? html`
            <div class="preview-section">
              <h4>Captured State Preview</h4>
              <div class="preview-content">${this.generatedMarkdown}</div>
            </div>
          `
        : html`
            <div class="preview-section">
              <h4>Report Info</h4>
              <div class="preview-content">
                Page: ${this.pageState?.pageName || "Unknown"} URL:
                ${this.pageState?.url || window.location.href} Screen:
                ${this.pageState?.screenWidth || window.innerWidth} x
                ${this.pageState?.screenHeight || window.innerHeight}
              </div>
            </div>
          `}

      <div class="actions">
        <wa-button
          variant="neutral"
          @click=${this._handleClose}
          ?disabled=${this.isSubmitting}
        >
          Cancel
        </wa-button>
        <wa-button
          variant="brand"
          @click=${this._handleSubmit}
          ?loading=${this.isSubmitting}
        >
          <wa-icon slot="prefix" name="paper-plane"></wa-icon>
          Submit Report
        </wa-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "debug-dialog": DebugDialog;
  }
}
